import React from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, Sparkles, Film, Award, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { ShowreelVideo } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface ShowreelProps {
  url?: string;
  embedCode?: string;
  showreels?: ShowreelVideo[];
}

export function Showreel({ url, embedCode, showreels }: ShowreelProps) {
  const { t } = useLanguage();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Normalize list of videos from prop or fallback to single
  const videos = React.useMemo(() => {
    if (showreels && showreels.length > 0) {
      return showreels;
    }
    return [
      {
        id: "default",
        title: "Bande-Démo Principale",
        url: url || "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
        embedCode: embedCode || "",
        thumbnailUrl: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=600&q=80"
      }
    ];
  }, [showreels, url, embedCode]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [carouselStart, setCarouselStart] = React.useState(0);
  const [isEmbedActivated, setIsEmbedActivated] = React.useState(false);

  // Auto-correct out-of-range index
  React.useEffect(() => {
    if (activeIndex >= videos.length) {
      setActiveIndex(0);
    }
  }, [videos, activeIndex]);

  // Pause/reset video player state when changing active video
  React.useEffect(() => {
    setIsPlaying(false);
    setIsEmbedActivated(false);
    if (videoRef.current) {
      videoRef.current.pause();
      try {
        videoRef.current.load();
      } catch (e) {
        console.warn("Could not reload video ref", e);
      }
    }
  }, [activeIndex]);

  const currentVideo = videos[activeIndex] || videos[0] || { url: "", embedCode: "", title: "" };
  const activeUrl = currentVideo.url || "";
  const currentEmbedCode = currentVideo.embedCode || "";

  // Helper to get YouTube thumbnail or fallback
  const getYouTubeThumbnail = (link: string, fallbackThumb?: string) => {
    if (fallbackThumb && fallbackThumb.trim() && !fallbackThumb.includes('unsplash.com/photo-1511285560929')) {
      return fallbackThumb;
    }
    try {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = link.match(regExp);
      const id = (match && match[2].length === 11) ? match[2] : null;
      if (id) {
        return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      }
    } catch (e) {
      // ignore
    }
    return fallbackThumb || "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80";
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => console.log("Video play interrupted", err));
    }
  };

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  // Extract video ID for YouTube or Vimeo embed if needed,
  // but if it's a direct mp4 (o2switch), we use native HTML5 video with H.265 & H.264 dual sources.
  const isEmbed = currentVideo.sourceType === 'youtube' || 
    (currentVideo.sourceType !== 'direct' && (
      activeUrl.includes('youtube.com') || 
      activeUrl.includes('youtu.be') || 
      activeUrl.includes('vimeo.com') || 
      activeUrl.includes('/embed/')
    ));

  const getEmbedUrl = (link: string) => {
    try {
      if (link.includes('/embed/')) {
        return link;
      }

      let videoId = '';
      let startTime = '';

      const parsedUrl = new URL(link.startsWith('http') ? link : `https://${link}`);
      
      const tParam = parsedUrl.searchParams.get('t') || parsedUrl.searchParams.get('start');
      if (tParam) {
        startTime = tParam.replace('s', '');
      }

      if (link.includes('youtu.be/')) {
        const segments = parsedUrl.pathname.split('/');
        videoId = segments[segments.length - 1];
      } else if (link.includes('youtube.com')) {
        videoId = parsedUrl.searchParams.get('v') || '';
        if (!videoId && parsedUrl.pathname.includes('/embed/')) {
          const segments = parsedUrl.pathname.split('/');
          videoId = segments[segments.length - 1];
        }
      } else if (link.includes('vimeo.com')) {
        const segments = parsedUrl.pathname.split('/');
        const id = segments[segments.length - 1];
        return id ? `https://player.vimeo.com/video/${id}?autoplay=1&muted=1` : link;
      }

      if (videoId) {
        let embed = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&controls=1&rel=0`;
        if (startTime) {
          embed += `&start=${startTime}`;
        }
        return embed;
      }
    } catch (err) {
      console.warn("Could not parse url with URL constructor, falling back to regex", err);
    }

    if (link.includes('youtube.com') || link.includes('youtu.be')) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = link.match(regExp);
      const id = (match && match[2].length === 11) ? match[2] : null;
      return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&controls=1&rel=0` : link;
    }
    if (link.includes('vimeo.com')) {
      const id = link.split('/').pop();
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1&muted=1` : link;
    }
    return link;
  };

  return (
    <section className="py-24 bg-theme-bg relative overflow-hidden border-t border-theme-border z-10" id="showreel-section">
      
      {/* Subtle Background Radial Highlights */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-theme-accent/3 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-xs uppercase tracking-[0.3em] text-theme-accent font-mono block mb-3">
            {t('showreel_intro_tag')}
          </span>
          <h2 className="text-3xl md:text-5xl font-serif text-theme-text font-light tracking-wide mb-4">
            {t('showreel_intro_title')}
          </h2>
          <p className="text-sm text-theme-secondary max-w-2xl mx-auto font-light leading-relaxed">
            {t('showreel_intro_desc')}
          </p>
        </div>

        {/* Video Player Box */}
        <div className="relative aspect-[21/9] w-full bg-theme-card border border-theme-border overflow-hidden shadow-2xl group">
          
          {(currentEmbedCode && currentEmbedCode.trim() !== "") || isEmbed ? (
            !isEmbedActivated ? (
              /* Lightweight Facade (Click to Load Player) - Prevents initial page load slowdown */
              <div 
                className="w-full h-full relative cursor-pointer group/facade overflow-hidden"
                onClick={() => setIsEmbedActivated(true)}
              >
                <img
                  src={getYouTubeThumbnail(activeUrl, currentVideo.thumbnailUrl)}
                  alt={currentVideo.title || "Vidéo YouTube"}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover/facade:scale-105"
                />
                <div className="absolute inset-0 bg-black/50 group-hover/facade:bg-black/40 transition-colors" />

                {/* Central Play Button */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEmbedActivated(true);
                    }}
                    aria-label={`Charger et lire la vidéo : ${currentVideo.title}`}
                    title="Cliquer pour afficher la vidéo"
                    className="w-16 h-16 md:w-20 md:h-20 bg-theme-accent text-theme-bg rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(var(--theme-accent-rgb),0.5)] backdrop-blur-sm cursor-pointer"
                  >
                    <Play className="w-6 h-6 md:w-8 md:h-8 fill-theme-bg translate-x-0.5" />
                  </motion.button>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/90 bg-black/70 px-3 py-1 border border-white/10 backdrop-blur-md">
                    Lancer la vidéo
                  </span>
                </div>

                {/* External link fallback */}
                <div className="absolute bottom-4 right-4 z-20">
                  <a
                    href={activeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Ouvrir la vidéo sur YouTube dans un nouvel onglet"
                    className="px-4 py-2.5 bg-black/80 hover:bg-theme-accent hover:text-theme-bg text-white text-[10px] font-mono uppercase tracking-widest border border-white/10 backdrop-blur-md transition-all flex items-center gap-2 cursor-pointer shadow-xl"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-theme-accent" />
                    <span>{t('showreel_open_external')}</span>
                  </a>
                </div>
              </div>
            ) : currentEmbedCode && currentEmbedCode.trim() !== "" ? (
              <div 
                className="w-full h-full relative [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:border-none"
                dangerouslySetInnerHTML={{ __html: currentEmbedCode }}
              />
            ) : (
              <div className="w-full h-full relative">
                <iframe
                  src={getEmbedUrl(activeUrl) || undefined}
                  className="w-full h-full pointer-events-auto border-none"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={currentVideo.title || t('showreel_intro_title')}
                  referrerPolicy="no-referrer"
                />
                {/* Fallback button overlay when YouTube embeds are blocked or have restricted playback */}
                <div className="absolute bottom-4 right-4 z-20">
                  <a
                    href={activeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    aria-label="Ouvrir la vidéo sur YouTube dans un nouvel onglet"
                    className="px-4 py-2.5 bg-black/80 hover:bg-theme-accent hover:text-theme-bg text-white text-[10px] font-mono uppercase tracking-widest border border-white/10 backdrop-blur-md transition-all flex items-center gap-2 cursor-pointer shadow-xl"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-theme-accent" />
                    <span>{t('showreel_open_external')}</span>
                  </a>
                </div>
              </div>
            )
          ) : (
            <div className="w-full h-full cursor-pointer relative" onClick={handlePlayPause}>
              <video
                ref={videoRef}
                loop
                muted={isMuted}
                playsInline
                preload="metadata"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              >
                {currentVideo.h265Url && currentVideo.h265Url.trim() !== '' && (
                  <source src={currentVideo.h265Url} type='video/mp4; codecs="hevc, mp4a.40.2"' />
                )}
                {currentVideo.h264Url && currentVideo.h264Url.trim() !== '' && (
                  <source src={currentVideo.h264Url} type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
                )}
                {activeUrl && activeUrl.trim() !== '' && (
                  <source src={activeUrl} type="video/mp4" />
                )}
              </video>

              {/* Shadow Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 opacity-60 transition-opacity group-hover:opacity-75" />

              {/* Central Large Play Button when not playing */}
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-16 h-16 md:w-20 md:h-20 bg-theme-accent text-theme-bg rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(var(--theme-accent-rgb),0.4)] backdrop-blur-sm"
                  >
                    <Play className="w-6 h-6 md:w-8 md:h-8 fill-theme-bg translate-x-0.5" />
                  </motion.div>
                </div>
              )}

              {/* Bottom Custom Playback Bar controls */}
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 flex items-center justify-between z-20 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
                    aria-label={isPlaying ? "Mettre en pause la vidéo" : "Lancer la vidéo"}
                    title={isPlaying ? "Pause" : "Lecture"}
                    className="p-2.5 bg-black/50 hover:bg-theme-accent hover:text-theme-bg border border-white/10 text-white rounded-none transition-all cursor-pointer"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleMuteToggle}
                    aria-label={isMuted ? "Activer le son" : "Désactiver le son"}
                    title={isMuted ? "Unmute" : "Mute"}
                    className="p-2.5 bg-black/50 hover:bg-theme-accent hover:text-theme-bg border border-white/10 text-white rounded-none transition-all cursor-pointer"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono tracking-widest text-white/70 uppercase bg-black/40 px-2.5 py-1">
                    {t('showreel_ambient_badge')}
                  </span>
                  <button
                    onClick={handleFullscreen}
                    aria-label="Passer en plein écran"
                    title="Plein écran"
                    className="p-2.5 bg-black/50 hover:bg-theme-accent hover:text-theme-bg border border-white/10 text-white rounded-none transition-all cursor-pointer"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quick aesthetic top badge */}
          <div className="absolute top-4 left-4 flex gap-2 pointer-events-none z-20">
            <span className="px-2.5 py-1 bg-black/60 backdrop-blur-md text-[9px] font-mono uppercase tracking-widest text-theme-accent border border-theme-accent/20 flex items-center gap-1.5">
              <Film className="w-3 h-3 text-theme-accent animate-pulse" />
              {t('showreel_demo_badge')} {currentVideo.title}
            </span>
          </div>
        </div>

        {/* Thumbnails Carousel List below the video */}
        {videos.length > 1 && (
          <div className="mt-6 flex items-center gap-2 md:gap-4 bg-theme-card/30 border border-theme-border/50 p-3 md:p-4 rounded-none">
            {/* Left Button */}
            <button
              onClick={() => setCarouselStart(prev => Math.max(prev - 1, 0))}
              disabled={carouselStart === 0}
              aria-label="Voir les vignettes vidéos précédentes"
              className="p-2 bg-black/40 border border-white/5 text-white/70 hover:text-theme-accent hover:border-theme-accent/50 disabled:opacity-35 disabled:cursor-not-allowed transition-all cursor-pointer rounded-none"
              title="Vignettes précédentes"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Thumbnails list displaying max 3 at a time */}
            <div className="flex-1 grid grid-cols-3 gap-3 md:gap-4 overflow-hidden">
              {videos.slice(carouselStart, carouselStart + 3).map((video, idx) => {
                const globalIndex = carouselStart + idx;
                const isActive = globalIndex === activeIndex;
                const fallbackThumb = "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=400&q=80";

                return (
                  <button
                    key={video.id || globalIndex}
                    onClick={() => setActiveIndex(globalIndex)}
                    className={`relative aspect-[16/9] w-full overflow-hidden border transition-all text-left group/thumb cursor-pointer ${
                      isActive
                        ? "border-theme-accent ring-1 ring-theme-accent/30"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    {/* Thumbnail Image */}
                    <img
                      src={(video.thumbnailUrl || fallbackThumb) || undefined}
                      alt={video.title}
                      referrerPolicy="no-referrer"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/thumb:scale-105 select-none pointer-events-none"
                    />

                    {/* Dark gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />

                    {/* Active indicator dot */}
                    {isActive && (
                      <div className="absolute top-2 right-2 w-2 h-2 bg-theme-accent rounded-full animate-pulse" />
                    )}

                    {/* Title Overlay */}
                    <div className="absolute bottom-2 left-2 right-2 z-10">
                      <p className={`text-[9px] md:text-[11px] font-mono tracking-wider line-clamp-1 transition-colors font-medium ${isActive ? "text-theme-accent" : "text-white group-hover/thumb:text-theme-accent"}`}>
                        {video.title}
                      </p>
                      <p className="text-[7px] md:text-[8px] font-mono text-zinc-400 mt-0.5 uppercase tracking-widest">
                        {video.embedCode ? "Intégration iframe" : (video.url.includes("youtube") || video.url.includes("youtu.be") ? "YouTube" : "Fichier Vidéo")}
                      </p>
                    </div>

                    {/* Ambient glow highlight on hover */}
                    <div className="absolute inset-0 bg-theme-accent/5 opacity-0 group-hover/thumb:opacity-100 transition-opacity" />
                  </button>
                );
              })}
            </div>

            {/* Right Button */}
            <button
              onClick={() => setCarouselStart(prev => Math.min(prev + 1, videos.length - 3))}
              disabled={carouselStart >= videos.length - 3}
              aria-label="Voir les vignettes vidéos suivantes"
              className="p-2 bg-black/40 border border-white/5 text-white/70 hover:text-theme-accent hover:border-theme-accent/50 disabled:opacity-35 disabled:cursor-not-allowed transition-all cursor-pointer rounded-none"
              title="Vignettes suivantes"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Marketing conversion pitch cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          <div className="bg-theme-card border border-theme-border p-6 flex items-start gap-4">
            <div className="p-3 bg-theme-accent/10 border border-theme-accent/20 text-theme-accent">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-mono uppercase tracking-wider text-theme-text font-bold mb-1">{t('showreel_pitch1_title')}</h4>
              <p className="text-xs text-theme-secondary leading-relaxed">
                {t('showreel_pitch1_desc')}
              </p>
            </div>
          </div>

          <div className="bg-theme-card border border-theme-border p-6 flex items-start gap-4">
            <div className="p-3 bg-theme-accent/10 border border-theme-accent/20 text-theme-accent">
              <Film className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-mono uppercase tracking-wider text-theme-text font-bold mb-1">{t('showreel_pitch2_title')}</h4>
              <p className="text-xs text-theme-secondary leading-relaxed">
                {t('showreel_pitch2_desc')}
              </p>
            </div>
          </div>

          <div className="bg-theme-card border border-theme-border p-6 flex items-start gap-4">
            <div className="p-3 bg-theme-accent/10 border border-theme-accent/20 text-theme-accent">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-mono uppercase tracking-wider text-theme-text font-bold mb-1">{t('showreel_pitch3_title')}</h4>
              <p className="text-xs text-theme-secondary leading-relaxed">
                {t('showreel_pitch3_desc')}
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
